import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import ThemeToggle from '../ThemeToggle/ThemeToggle';
import LanguageToggle from '../LanguageToggle/LanguageToggle';
import { useLanguage } from '../../contexts/LanguageContext';
import { useMessageModal } from '../../contexts/MessageModalContext';
import { scrollToId } from '../../utils/scrollToSection';
import { useLenisRef } from '../../context/ScrollContext';
import { CONNECTION_DATA } from '../../constants';
import content from '../../i18n/content';
import dvirAvatar from '../../assets/dvir-avatar.png';
import { GitHub, LinkedIn, Download, Email } from '@mui/icons-material';
import { MessageCircle } from 'lucide-react';
import './Navbar.css';

const NAV_ITEMS = ['about', 'experience', 'education', 'projects', 'skills'];

export default function Navbar() {
  const { lang } = useLanguage();
  const t = content[lang].nav;
  const { openModal } = useMessageModal();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);

  const handleEmailCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(CONNECTION_DATA.email.title);
      setEmailCopied(true);
      setTimeout(() => setEmailCopied(false), 2000);
    } catch {}
  }, []);
  const drawerRef = useRef(null);
  const lenisRef = useLenisRef();

  const handleNavClick = (sectionId) => {
    setIsMenuOpen(false);
    if (location.pathname === '/') {
      scrollToId(sectionId);
    } else {
      navigate('/', { state: { scrollTo: sectionId } });
    }
  };

  // Escape key closes drawer — no dependency on isMenuOpen needed
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setIsMenuOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Stop Lenis (not overflow:hidden) — avoids scrollbar-removal layout shift.
  // touchmove block handles iOS native touch scroll outside the drawer.
  // drawer-open class signals CurtainOverlay to ignore wheel/touch events.
  useEffect(() => {
    if (!isMenuOpen) return;
    const lenis = lenisRef.current;
    lenis?.stop();
    document.body.classList.add('drawer-open');

    const preventTouch = (e) => {
      if (drawerRef.current?.contains(e.target)) return;
      e.preventDefault();
    };
    document.addEventListener('touchmove', preventTouch, { passive: false });

    return () => {
      lenis?.start();
      document.body.classList.remove('drawer-open');
      document.removeEventListener('touchmove', preventTouch);
    };
  }, [isMenuOpen, lenisRef]);

  // Focus trap + auto-focus close button on open
  useEffect(() => {
    if (!isMenuOpen || !drawerRef.current) return;
    const drawer = drawerRef.current;
    const focusable = drawer.querySelectorAll(
      'button, a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    // Focus the close button (always first) when drawer opens
    first?.focus();

    const handleKeyDown = (e) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    drawer.addEventListener('keydown', handleKeyDown);
    return () => drawer.removeEventListener('keydown', handleKeyDown);
  }, [isMenuOpen]);

  return (
    <>
      <nav className="navbar">
        <Link to="/" className="navbar-logo" aria-label="Dvir Magen — home">
          <img src={dvirAvatar} alt="Dvir Magen" className="navbar-avatar" />
        </Link>

        <ul className="navbar-links">
          {NAV_ITEMS.map((key) => (
            <li key={key}>
              <button type="button" onClick={() => handleNavClick(key)}>
                {t[key]}
              </button>
            </li>
          ))}
        </ul>

        <div className="navbar-actions">
          <div className="navbar-toggles">
            <LanguageToggle />
            <ThemeToggle />
          </div>
          <a
            href={CONNECTION_DATA.cv.url}
            target="_blank"
            rel="noopener noreferrer"
            className="navbar-cta"
          >
            {t.resume}
          </a>
          <button
            type="button"
            className={`navbar-hamburger ${isMenuOpen ? 'open' : ''}`}
            onClick={() => setIsMenuOpen((open) => !open)}
            aria-label="Toggle navigation menu"
            aria-expanded={isMenuOpen}
            aria-controls="nav-drawer"
          >
            <span /><span /><span />
          </button>
        </div>
      </nav>

      {/* Scrim behind drawer */}
      <div
        className={`drawer-scrim ${isMenuOpen ? 'open' : ''}`}
        onClick={() => setIsMenuOpen(false)}
        role="presentation"
        data-testid="drawer-scrim"
      />

      {/* Side drawer */}
      <aside
        id="nav-drawer"
        className={`nav-drawer ${isMenuOpen ? 'open' : ''}`}
        aria-hidden={!isMenuOpen}
        aria-label="Navigation menu"
        ref={drawerRef}
        {...(!isMenuOpen && { inert: '' })}
      >
        <button
          type="button"
          className="drawer-close"
          onClick={() => setIsMenuOpen(false)}
          aria-label="Close navigation menu"
        >
          <span /><span />
        </button>

        <div className="drawer-divider" />

        <ul className="drawer-nav-links">
          {NAV_ITEMS.map((key, index) => (
            <li key={key} style={{ '--item-index': index }}>
              <button type="button" onClick={() => handleNavClick(key)}>
                {t[key]}
              </button>
            </li>
          ))}
        </ul>

        <div className="drawer-divider" />

        <div className="drawer-actions">
          <LanguageToggle />
          <ThemeToggle />
        </div>

        <div className="drawer-social-row">
          <button
            type="button"
            className="drawer-social-btn"
            onClick={() => { openModal(); setIsMenuOpen(false); }}
            aria-label="Send a direct message"
            data-tooltip="Message"
          >
            <MessageCircle size={20} />
          </button>
          <button
            type="button"
            className="drawer-social-btn"
            onClick={handleEmailCopy}
            aria-label="Copy email address"
            data-tooltip="Email"
          >
            <Email fontSize="small" />
          </button>
          <a
            href={CONNECTION_DATA.github.url}
            target="_blank"
            rel="noopener noreferrer"
            className="drawer-social-btn"
            aria-label="GitHub profile"
            data-tooltip="GitHub"
          >
            <GitHub fontSize="small" />
          </a>
          <a
            href={CONNECTION_DATA.linkedin.url}
            target="_blank"
            rel="noopener noreferrer"
            className="drawer-social-btn"
            aria-label="LinkedIn profile"
            data-tooltip="LinkedIn"
          >
            <LinkedIn fontSize="small" />
          </a>
          <a
            href={CONNECTION_DATA.cv.url}
            target="_blank"
            rel="noopener noreferrer"
            className="drawer-social-btn"
            aria-label="Download CV"
            data-tooltip="Resume"
          >
            <Download fontSize="small" />
          </a>
        </div>
        <div className={`drawer-email-log ${emailCopied ? 'visible' : ''}`}>
          ✓ {CONNECTION_DATA.email.title} copied
        </div>
      </aside>
    </>
  );
}
